import React, { useState, useRef, useCallback } from 'react';
import { Paperclip, Upload, X, FileText, Image, Film, Award, File } from 'lucide-react';
import { uploadEvidence } from '../services/auditFormEvidenceService';

/**
 * Returns a Lucide icon component for a given MIME type.
 */
const iconForMime = (mime) => {
  if (!mime) return File;
  if (mime.startsWith('image/')) return Image;
  if (mime.startsWith('video/')) return Film;
  if (mime === 'application/pdf') return FileText;
  if (mime.includes('certificate')) return Award;
  return Paperclip;
};

/**
 * Format bytes to human-readable string.
 */
const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const EVIDENCE_TYPES = ['Photo', 'Document', 'Video', 'Certificate', 'Other'];

const AuditEvidenceModal = ({
  isOpen,
  onClose,
  projectId,
  formId,
  processId,
  scopeKey,
  documentCode,
  onUploadSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [evidenceType, setEvidenceType] = useState('Document');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSelectedFile(file);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setError('');
    setUploading(true);
    const result = await uploadEvidence({
      file: selectedFile,
      projectId,
      formId: formId || null,
      processId: processId || null,
      scopeKey,
      documentCode,
      evidenceType,
    });
    setUploading(false);
    if (!result.success && result.message) {
      setError(result.message);
      return;
    }
    if (onUploadSuccess) onUploadSuccess(result);
    setSelectedFile(null);
    setEvidenceType('Document');
    onClose();
  };

  const handleClose = () => {
    if (uploading) return;
    setSelectedFile(null);
    setError('');
    setEvidenceType('Document');
    onClose();
  };

  if (!isOpen) return null;

  const FileIcon = iconForMime(selectedFile?.type);

  return (
    <div
      className="modal show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1055 }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>

          {/* Header */}
          <div
            className="modal-header border-0 px-4 py-3"
            style={{ background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 100%)' }}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-2"
                style={{ width: 38, height: 38, background: 'rgba(255,255,255,0.18)', flexShrink: 0 }}
              >
                <Paperclip size={18} color="white" />
              </div>
              <div>
                <h5 className="modal-title text-white fw-bold mb-0" style={{ fontSize: '1rem' }}>
                  Attach Evidence
                </h5>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              aria-label="Close"
            />
          </div>

          {/* Body */}
          <div className="modal-body px-4 py-3" style={{ background: '#f8f9fa' }}>

            {/* Scope / doc badges */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <span className="badge" style={{ background: '#1f656c', color: 'white', fontSize: '0.72rem' }}>
                {scopeKey || '—'}
              </span>
              <span className="badge" style={{ background: '#2a8a93', color: 'white', fontSize: '0.72rem' }}>
                {documentCode}
              </span>
            </div>

            {/* Drag-and-drop zone */}
            <div
              className={`border rounded-3 d-flex flex-column align-items-center justify-content-center py-4 mb-3 ${dragOver ? 'border-primary' : 'border-secondary'} ${selectedFile ? 'bg-light' : ''}`}
              style={{ borderStyle: selectedFile ? 'solid' : 'dashed', cursor: 'pointer', minHeight: 120, transition: 'border-color .15s, background .15s' }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <div className="text-center">
                  <FileIcon size={32} className="text-secondary mb-2" />
                  <div className="fw-medium text-dark" style={{ fontSize: '0.88rem' }}>{selectedFile.name}</div>
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>{formatSize(selectedFile.size)}</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger mt-2"
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={28} className="text-secondary mb-2" />
                  <div className="text-secondary fw-medium" style={{ fontSize: '0.85rem' }}>
                    Drop file here or click to browse
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Images, PDF, video — max 10 MB
                  </div>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,video/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>

            {/* Evidence type */}
            <div className="mb-3">
              <label className="form-label fw-medium text-secondary" style={{ fontSize: '0.82rem' }}>
                Evidence Type
              </label>
              <select
                className="form-select form-select-sm"
                value={evidenceType}
                onChange={(e) => setEvidenceType(e.target.value)}
              >
                {EVIDENCE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Error alert */}
            {error && (
              <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem' }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-top bg-white px-4 py-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm px-4"
              onClick={handleClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm px-4 d-flex align-items-center gap-2"
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={14} />
                  Upload
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuditEvidenceModal;
